/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Input } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadHubAuditEntryReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { SERVICE_TYPE_OPTIONS, TRAVEL_BY_OPTIONS } from "../../../../constants/reportConstants";

const HubAuditEntryReport = () => {
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");
    // Set default date range to last 7 days
    const defaultDateRange = [
        moment().subtract(1, "days"),
        moment()
    ];

    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [entryTypeList, setEntryTypeList] = useState([
        { label: "Manual", value: "manual" },
        { label: "Auto", value: "auto" }
    ]);
    const [selectedEntryTypes, setSelectedEntryTypes] = useState([]);
    const [awbNumbers, setAwbNumbers] = useState("");
    const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
    const [selectedTravelBy, setSelectedTravelBy] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();

    // Request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [headerHubList, setHeaderHubList] = useState([]);

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Hub Audit Entry Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                // setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([newActiveHubId]);
            } else {
                setSelectedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Set default hub based on logged-in user — always enforced for hub-restricted roles
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            
            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedHubs([]);
            } else {
                setSelectedHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    useEffect(() => {
        if (!hubUser) {
            return;
        }
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
            return;
        }
        // Clear data if date range is cleared
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
            setLastsynced(null);
            setLoading(false);
            return;
        }
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedHubs, selectedEntryTypes, awbNumbers, selectedServiceTypes, selectedTravelBy, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        // Create new request with cancellation support
        const { signal, requestId } = createNewRequest("hub-audit-entry-report");

        setLoading(true);
        try {
            // Resolve Hubs (selectedHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedHubIds = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedHubData = activeHubOptions.filter(hub => resolvedHubIds?.includes(hub.value));
            // Parse comma-separated AWB numbers
            const awbNumbersArray = awbNumbers ? awbNumbers.split(",").map(s => s.trim()).filter(Boolean) : [];

            const params = {
                ...(selectedHubData?.length > 0 && { auditedHubs: selectedHubData.map(hub => hub.label) }),
                ...(selectedEntryTypes?.length > 0 && { entryTypes: selectedEntryTypes }),
                ...(awbNumbersArray.length > 0 && { awbNumbers: awbNumbersArray }),
                ...(selectedServiceTypes?.length > 0 && { serviceTypes: selectedServiceTypes }),
                ...(selectedTravelBy?.length > 0 && { travelBy: selectedTravelBy }),
                ...(dateRange?.[0] && { startDate: dateRange[0].format("YYYY-MM-DD") }),
                ...(dateRange?.[1] && { endDate: dateRange[1].format("YYYY-MM-DD") }),
                page: pagination.current,
                pageSize: pagination.pageSize
            };
            const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getHubAuditEntryReport(params, { signal });

            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring response");
                return;
            }

            setReportData(data);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextScheduledRefresh);
            setPagination(prev => ({
                ...prev,
                total,
                current: page,
                pageSize
            }));
        } catch (error) {
            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring error");
                return;
            }

            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled");
                return;
            }

            console.error("Error fetching report data:", error);
            message.error("Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            // Only update loading state if this is still the latest request
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleDownload = async () => {
        try {
            setDownloadLoading(true);
            // Resolve Hubs (selectedHubs) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedHubIds = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedHubData = activeHubOptions.filter(hub => resolvedHubIds?.includes(hub.value));
            // Parse comma-separated AWB numbers
            const awbNumbersArray = awbNumbers ? awbNumbers.split(",").map(s => s.trim()).filter(Boolean) : [];

            const downloadParams = {
                startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
                endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
                entryTypes: selectedEntryTypes,
                auditedHubs: selectedHubData.map(hub => hub.label),
                ...(awbNumbersArray.length > 0 && { awbNumbers: awbNumbersArray }),
                ...(selectedServiceTypes?.length > 0 && { serviceTypes: selectedServiceTypes }),
                ...(selectedTravelBy?.length > 0 && { travelBy: selectedTravelBy })
            };

            await downloadHubAuditEntryReport(downloadParams);
        } catch (error) {
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Document No", dataIndex: "documentNo", key: "documentNo", sorter: true, width: 140,
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        { title: "Audit Date", dataIndex: "auditDate", key: "auditDate", sorter: true, width: 120 },
        { title: "Booking Date", dataIndex: "bookingDate", key: "bookingDate", sorter: true, width: 120 },
        { title: "From Center", dataIndex: "fromCenter", key: "fromCenter", sorter: true, width: 120 },
        { title: "To Center", dataIndex: "toCenter", key: "toCenter", sorter: true, width: 120 },
        { title: "Document Type", dataIndex: "documentType", key: "documentType", sorter: true, width: 120 },
        { title: "Service Type", dataIndex: "serviceType", key: "serviceType", sorter: true, width: 120 },
        { title: "Travel By", dataIndex: "travelBy", key: "travelBy", sorter: true, width: 120 },
        { title: "Freight Charges", dataIndex: "freightCharges", key: "freightCharges", sorter: true, width: 120 },
        { title: "Booking Weight (kg)", dataIndex: "bookingWeight", key: "bookingWeight", sorter: true, width: 150 },
        { title: "Audited Weight (kg)", dataIndex: "auditedWeight", key: "auditedWeight", sorter: true, width: 150 },
        { title: "Weight Difference (kg)", dataIndex: "weightDifference", key: "weightDifference", sorter: true, width: 160 },
        { title: "Weight Difference %", dataIndex: "weightDifferencePercentage", key: "weightDifferencePercentage", sorter: true, width: 160 },
        { title: "Volumetric Booked Weight (kg)", dataIndex: "volumetricBookedWeight", key: "volumetricBookedWeight", sorter: true, width: 200 },
        { title: "Volumetric Audited Weight (kg)", dataIndex: "volumetricAuditedWeight", key: "volumetricAuditedWeight", sorter: true, width: 200 },
        { title: "Audited Hub", dataIndex: "auditedHub", key: "auditedHub", sorter: true, width: 140 },
        { title: "Length", dataIndex: "length", key: "length", sorter: true, width: 100 },
        { title: "Width", dataIndex: "width", key: "width", sorter: true, width: 100 },
        { title: "Height", dataIndex: "height", key: "height", sorter: true, width: 100 },
        { title: "Cash Account", dataIndex: "cashAccount", key: "cashAccount", sorter: true, width: 130 },
        { title: "Manual/Auto", dataIndex: "manualAuto", key: "manualAuto", sorter: true, width: 130 },
        { title: "Status", dataIndex: "status", key: "status", sorter: true, width: 120 },
    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading
                    title="HUB Audit Entry Report"
                >
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From & To Date</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={setDateRange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Audited Hub</div>
                        <DropDownComponent
                            placeholder="Select Audited Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Entry Type</div>
                        <DropDownComponent
                            placeholder="Entry Type"
                            options={entryTypeList}
                            onChange={setSelectedEntryTypes}
                            value={selectedEntryTypes}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">AWB Numbers</div>
                        <Input
                            placeholder="Enter comma separated AWB numbers"
                            value={awbNumbers}
                            onChange={(e) => setAwbNumbers(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Service Types</div>
                        <DropDownComponent
                            placeholder="Select Service Types"
                            options={SERVICE_TYPE_OPTIONS.filter(opt => opt.value !== "ALL")}
                            onChange={setSelectedServiceTypes}
                            value={selectedServiceTypes}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Travel By</div>
                        <DropDownComponent
                            placeholder="Select Travel By"
                            options={TRAVEL_BY_OPTIONS}
                            onChange={setSelectedTravelBy}
                            value={selectedTravelBy}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} style={{ display: "flex", alignItems: "flex-end" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={!dateRange?.[0] || !dateRange?.[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                sticky
            />
        </div>
    );
};

export default HubAuditEntryReport;

